import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Users, Archive, Clock, MessageSquare, ChevronDown, ChevronUp, Calendar, Pencil, X as XIcon, Send, CheckCircle2, Link2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  getBoardMembers, createBoardMember, archiveBoardMember, unarchiveBoardMember,
  getBoardMessages, getPendingBoardMessages, getClosedBoardMessages,
  activatePendingBoardMessages, createBoardMessage, updateBoardMessage,
  respondToBoardMessage, closeBoardMessage, snoozeBoardMessage, deleteBoardMessage,
  getBoardUnreadCount, markBoardSeen,
  getProperties, getUnits, getTenants, getLeads, getHousekeepers, getMaintenancePersonnel,
} from '@/lib/api';
import { useAppAuth } from '@/contexts/AppAuthContext';
import { NewMessagePanel } from './NewMessagePanel';
import { TeamMembersDialog } from './TeamMembersDialog';
import { ClosedMessagesDialog } from './ClosedMessagesDialog';
import { PendingMessagesDialog } from './PendingMessagesDialog';
import { MessageCard } from './MessageCard';
import { EditMessageDialog } from './EditMessageDialog';

export function MessageBoard({ compact = false }) {
  const { user, isAdmin } = useAppAuth();
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);

  // Admin-only "All / Mine" toggle. Users always see only their own scope
  // (the backend enforces this regardless of the param). Default to "all"
  // for admins so they keep seeing everything they did pre-Phase-3.
  const [scope, setScope] = useState('all');

  // Dialogs
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [showTeamMembers, setShowTeamMembers] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);

  const fetchData = async () => {
    try {
      await activatePendingBoardMessages().catch(() => {});
      const params = isAdmin && scope === 'mine' ? { mine_only: true } : {};
      const [m, msgs, unreadRes] = await Promise.all([
        getBoardMembers(),
        getBoardMessages(params),
        getBoardUnreadCount().catch(() => ({ count: 0 })),
      ]);
      setMembers(m);
      setMessages(msgs);
      setUnread(unreadRes?.count || 0);
    } catch { toast.error('Failed to load message board'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [scope, isAdmin]);

  // Clear the badge once the user actually interacts with the board.
  // Stamp `mark-seen` and zero the local count, but keep the in-memory
  // messages — the user is now caught up.
  const clearUnread = async () => {
    if (unread <= 0) return;
    setUnread(0);
    markBoardSeen().catch(() => {});
  };

  const activeMembers = useMemo(() => members.filter(m => !m.is_archived), [members]);

  const handleRespond = async (messageId, text, byId, toId) => {
    try {
      await respondToBoardMessage(messageId, { text, by: byId, to: toId });
      toast.success('Response sent');
      clearUnread();
      fetchData();
    } catch { toast.error('Failed to send response'); }
  };

  const handleClose = async (messageId, byName) => {
    try {
      await closeBoardMessage(messageId, byName);
      toast.success('Conversation closed');
      clearUnread();
      fetchData();
    } catch { toast.error('Failed to close'); }
  };

  const handleSnooze = async (messageId, until) => {
    try {
      await snoozeBoardMessage(messageId, until);
      toast.success(`Snoozed until ${new Date(until + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
      clearUnread();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to snooze');
    }
  };

  const handleDelete = async (messageId) => {
    try {
      await deleteBoardMessage(messageId);
      toast.success('Message deleted');
      clearUnread();
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-40 text-muted-foreground text-[13px]">Loading message board...</div>;
  }

  return (
    <div className="space-y-4" data-testid="message-board">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2">
          Message Board
          {unread > 0 && (
            <button
              type="button"
              onClick={clearUnread}
              title="Click to mark as read"
              data-testid="message-board-unread-badge"
              className="bg-rose-500 text-white text-[10px] h-5 px-1.5 rounded-full font-bold inline-flex items-center hover:bg-rose-600 transition-colors"
            >
              {unread} new
            </button>
          )}
        </h2>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-[11px]" data-testid="message-board-scope-toggle">
              <button
                type="button"
                onClick={() => setScope('all')}
                className={`px-2.5 py-1 ${scope === 'all' ? 'bg-emerald-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                data-testid="message-board-scope-all"
              >All</button>
              <button
                type="button"
                onClick={() => setScope('mine')}
                className={`px-2.5 py-1 border-l border-gray-200 ${scope === 'mine' ? 'bg-emerald-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                data-testid="message-board-scope-mine"
              >Mine</button>
            </div>
          )}
          <Button
            size="sm"
            className="h-7 text-[12px] bg-teal-600 hover:bg-teal-700"
            onClick={() => setShowNewMessage(true)}
            data-testid="new-message-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> New Message
          </Button>
        </div>
      </div>

      {/* Team member sections */}
      {activeMembers.length === 0 ? (
        <div className="text-center py-8 border border-dashed rounded-lg">
          <MessageSquare className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-[13px] text-gray-400">No team members yet</p>
          <Button size="sm" variant="outline" className="mt-2 h-7 text-[12px]" onClick={() => setShowTeamMembers(true)}>
            Add Team Member
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {activeMembers.map(member => {
            const memberMessages = messages.filter(m => m.current_list === member.id);
            return (
              <MemberSection
                key={member.id}
                member={member}
                messages={memberMessages}
                allMembers={activeMembers}
                onRespond={handleRespond}
                onClose={handleClose}
                onSnooze={handleSnooze}
                onDelete={handleDelete}
                onEdit={setEditingMessage}
                compact={compact}
              />
            );
          })}
        </div>
      )}

      {/* Bottom action buttons */}
      <div className="flex gap-4 pt-2 border-t">
        <button onClick={() => setShowTeamMembers(true)} className="text-[12px] text-gray-500 hover:text-gray-700 flex items-center gap-1.5 font-medium" data-testid="open-team-members">
          <Users className="h-3.5 w-3.5" /> Team Members
        </button>
        <button onClick={() => setShowClosed(true)} className="text-[12px] text-gray-500 hover:text-gray-700 flex items-center gap-1.5 font-medium" data-testid="open-closed-conversations">
          <CheckCircle2 className="h-3.5 w-3.5" /> Closed Conversations
        </button>
        <button onClick={() => setShowPending(true)} className="text-[12px] text-gray-500 hover:text-gray-700 flex items-center gap-1.5 font-medium" data-testid="open-pending-messages">
          <Clock className="h-3.5 w-3.5" /> Pending Messages
        </button>
      </div>

      {/* Panels & Dialogs */}
      <NewMessagePanel
        open={showNewMessage}
        onClose={() => setShowNewMessage(false)}
        members={activeMembers}
        onCreated={fetchData}
      />
      <TeamMembersDialog
        open={showTeamMembers}
        onOpenChange={setShowTeamMembers}
        onRefresh={fetchData}
      />
      <ClosedMessagesDialog
        open={showClosed}
        onOpenChange={setShowClosed}
      />
      <PendingMessagesDialog
        open={showPending}
        onOpenChange={setShowPending}
        onRefresh={fetchData}
      />
      {editingMessage && (
        <EditMessageDialog
          message={editingMessage}
          open={!!editingMessage}
          onOpenChange={(v) => { if (!v) setEditingMessage(null); }}
          onUpdated={fetchData}
        />
      )}
    </div>
  );
}

function MemberSection({ member, messages, allMembers, onRespond, onClose, onSnooze, onDelete, onEdit, compact }) {
  // Sections start collapsed by default — the dashboard surfaces a lot of
  // members at once and an auto-expanded list buries the rest of the page.
  const [collapsed, setCollapsed] = useState(true);

  return (
    <div className="border rounded-lg" data-testid={`member-section-${member.id}`}>
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors rounded-t-lg"
      >
        <span className="flex items-center gap-2">
          <span className="h-7 w-7 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-[12px] font-bold">
            {member.name.charAt(0).toUpperCase()}
          </span>
          <span className="text-[14px] font-semibold text-gray-800">{member.name}</span>
          {messages.length > 0 && (
            <Badge variant="secondary" className="text-[10px] h-5 px-1.5">{messages.length}</Badge>
          )}
        </span>
        {collapsed ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronUp className="h-4 w-4 text-gray-400" />}
      </button>
      {!collapsed && (
        <div className="px-3 pb-3 space-y-2 border-t">
          {messages.length === 0 ? (
            <p className="text-[12px] text-gray-400 py-2 text-center">No messages</p>
          ) : (
            messages.map(msg => (
              <MessageCard
                key={msg.id}
                message={msg}
                viewerMember={member}
                allMembers={allMembers}
                onRespond={onRespond}
                onClose={onClose}
                onSnooze={onSnooze}
                onDelete={onDelete}
                onEdit={onEdit}
                compact={compact}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default MessageBoard;
